import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Plus, Edit, Trash2, Save, X, Mail, Key, User } from 'lucide-react';

interface Monteur {
  id: string;
  name: string;
  funktion: string;
  user_id?: string;
}

interface UserAccount {
  monteur_id: string;
  email: string;
  password: string;
}

function MitarbeiterVerwaltung() {
  const [monteure, setMonteure] = useState<Monteur[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAccountForm, setShowAccountForm] = useState(false);
  const [selectedMonteurId, setSelectedMonteurId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<Omit<Monteur, 'id'>>({
    name: '',
    funktion: '',
  });

  const [accountFormData, setAccountFormData] = useState<UserAccount>({
    monteur_id: '',
    email: '',
    password: '',
  });

  useEffect(() => {
    loadMonteure();
  }, []);

  async function loadMonteure() {
    try {
      setLoading(true);
      
      // Get monteure and their linked user accounts
      const { data, error } = await supabase
        .from('monteure')
        .select(`
          id, 
          name,
          funktion,
          user_roles!monteur_id(user_id)
        `)
        .order('name');

      if (error) throw error;
      
      // Transform data to include user_id if available
      const transformedData = data?.map(item => {
        const user_id = item.user_roles && item.user_roles.length > 0 
          ? item.user_roles[0]?.user_id 
          : undefined;
        
        return {
          id: item.id,
          name: item.name,
          funktion: item.funktion || '',
          user_id
        };
      }) || [];
      
      setMonteure(transformedData);
    } catch (error) {
      console.error('Fehler beim Laden der Mitarbeiter:', error);
      alert('Fehler beim Laden der Mitarbeiter');
    } finally {
      setLoading(false);
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAccountInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setAccountFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingId) {
        // Update existing technician
        const { error } = await supabase
          .from('monteure')
          .update(formData)
          .eq('id', editingId);

        if (error) throw error;
        
        setMonteure(prev => 
          prev.map(monteur => 
            monteur.id === editingId ? { ...monteur, ...formData } : monteur
          )
        );
        setEditingId(null);
      } else {
        // Create new technician
        const { data, error } = await supabase
          .from('monteure')
          .insert([formData])
          .select()
          .single();

        if (error) throw error;
        setMonteure(prev => [...prev, data]);
      }
      
      // Reset form
      setFormData({
        name: '',
        funktion: '',
      });
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      alert('Fehler beim Speichern des Mitarbeiters');
    }
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // 1. Create a new user in Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: accountFormData.email,
        password: accountFormData.password,
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Benutzer konnte nicht erstellt werden');

      // 2. Create a record in user_roles table
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert([{
          user_id: authData.user.id,
          monteur_id: accountFormData.monteur_id,
          role: 'monteur'
        }]);

      if (roleError) throw roleError;

      // 3. Update local state
      setMonteure(prev => 
        prev.map(monteur => 
          monteur.id === accountFormData.monteur_id 
            ? { ...monteur, user_id: authData.user.id } 
            : monteur
        )
      );

      // 4. Reset form and close it
      setAccountFormData({
        monteur_id: '',
        email: '',
        password: '',
      });
      setShowAccountForm(false);
      setSelectedMonteurId(null);

      alert('Mitarbeiterkonto wurde erfolgreich erstellt!');
    } catch (error: any) {
      console.error('Fehler beim Erstellen des Kontos:', error);
      alert(`Fehler beim Erstellen des Kontos: ${error.message || 'Unbekannter Fehler'}`);
    }
  };

  const startEditing = (monteur: Monteur) => {
    setEditingId(monteur.id);
    setFormData({
      name: monteur.name,
      funktion: monteur.funktion || '',
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setFormData({
      name: '',
      funktion: '',
    });
  };

  const openAccountForm = (monteurId: string) => {
    const monteur = monteure.find(m => m.id === monteurId);
    if (!monteur) return;
    
    setSelectedMonteurId(monteurId);
    setAccountFormData({
      monteur_id: monteurId,
      email: '',
      password: '',
    });
    setShowAccountForm(true);
  };

  const closeAccountForm = () => {
    setShowAccountForm(false);
    setSelectedMonteurId(null);
    setAccountFormData({
      monteur_id: '',
      email: '',
      password: '',
    });
  };

  const deleteMonteur = async (id: string) => {
    if (!confirm('Sind Sie sicher, dass Sie diesen Mitarbeiter löschen möchten?')) {
      return;
    }
    
    try {
      // Check if technician has orders
      const { data: auftraege, error: auftraegeError } = await supabase
        .from('auftraege')
        .select('id')
        .eq('monteur_id', id);
        
      if (auftraegeError) throw auftraegeError;
      
      if (auftraege && auftraege.length > 0) {
        alert('Dieser Mitarbeiter hat zugeordnete Aufträge und kann nicht gelöscht werden.');
        return;
      }
      
      // Check if technician has a user account
      const monteur = monteure.find(m => m.id === id);
      if (monteur?.user_id) {
        // Delete from user_roles first
        const { error: roleError } = await supabase
          .from('user_roles')
          .delete()
          .eq('monteur_id', id);
          
        if (roleError) throw roleError;
      }
      
      // Delete the monteur
      const { error } = await supabase
        .from('monteure')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setMonteure(prev => prev.filter(monteur => monteur.id !== id));
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen des Mitarbeiters');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Mitarbeiterverwaltung</h1>
        
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700">Name des Mitarbeiters</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="Vor- und Nachname eingeben"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Funktion</label>
            <input
              type="text"
              name="funktion"
              value={formData.funktion}
              onChange={handleInputChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="z.B. Elektriker, Projektleiter, etc."
            />
          </div>
          
          <div className="md:col-span-2 flex justify-end space-x-2">
            {editingId && (
              <button
                type="button"
                onClick={cancelEditing}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 flex items-center gap-2"
              >
                <X size={20} />
                Abbrechen
              </button>
            )}
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              {editingId ? (
                <>
                  <Save size={20} />
                  Aktualisieren
                </>
              ) : (
                <>
                  <Plus size={20} />
                  Mitarbeiter hinzufügen
                </>
              )}
            </button>
          </div>
        </form>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Funktion</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Benutzerkonto</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aktionen</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {monteure.map((monteur) => (
                <tr key={monteur.id} className={editingId === monteur.id ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap">{monteur.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{monteur.funktion || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {monteur.user_id ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <User className="mr-1" size={12} />
                        Konto vorhanden
                      </span>
                    ) : (
                      <button
                        onClick={() => openAccountForm(monteur.id)}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200"
                      >
                        <Plus className="mr-1" size={12} />
                        Konto erstellen
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => startEditing(monteur)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Mitarbeiter bearbeiten"
                      >
                        <Edit size={20} />
                      </button>
                      <button
                        onClick={() => deleteMonteur(monteur.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Mitarbeiter löschen"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {monteure.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              Noch keine Mitarbeiter vorhanden
            </div>
          )}
        </div>
      </div>

      {/* Account creation modal */}
      {showAccountForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 flex items-center">
              <User className="mr-2" size={20} />
              Mitarbeiterkonto erstellen
            </h2>
            
            <p className="mb-4 text-sm text-gray-600">
              Erstellen Sie ein Benutzerkonto für {monteure.find(m => m.id === selectedMonteurId)?.name}. 
              Der Mitarbeiter kann sich mit diesen Zugangsdaten anmelden und seine Aufträge verwalten.
            </p>
            
            <form onSubmit={handleAccountSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">E-Mail</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={accountFormData.email}
                    onChange={handleAccountInputChange}
                    required
                    className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="mitarbeiter@example.com"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Passwort</label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    name="password"
                    value={accountFormData.password}
                    onChange={handleAccountInputChange}
                    required
                    minLength={6}
                    className="block w-full pl-10 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Mindestens 6 Zeichen"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Das Passwort muss mindestens 6 Zeichen lang sein.
                </p>
              </div>
              
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  type="button"
                  onClick={closeAccountForm}
                  className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 flex items-center gap-2"
                >
                  <X size={20} />
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={20} />
                  Konto erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MitarbeiterVerwaltung;